import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/shared/lib/cn";

// Themed Radix Select — brand tokens (NOT shadcn defaults). Compose like shadcn:
//   <Select value onValueChange><SelectTrigger><SelectValue placeholder/></SelectTrigger>
//     <SelectContent><SelectItem value="x">…</SelectItem></SelectContent></Select>
//
// Radix forbids an empty-string item value. For placeholder / clear options map "" <-> SELECT_NONE
// at the call site (e.g. ColorSelect "no colour", Notifications "any status").
export const SELECT_NONE = "__none__";

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export const SelectTrigger = forwardRef<
  ElementRef<typeof SelectPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { dense?: boolean; invalid?: boolean }
>(function SelectTrigger({ className, children, dense = false, invalid = false, ...props }, ref) {
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-control border bg-white text-left text-grey-800 outline-none transition-colors",
        "data-[placeholder]:text-grey-400 focus-visible:outline-none focus-visible:ring-1",
        "disabled:cursor-not-allowed disabled:opacity-50",
        dense ? "px-2 py-1 text-[12px]" : "px-3.5 py-2.5 text-[14px]",
        invalid
          ? "border-red focus-visible:border-red focus-visible:ring-red-border"
          : "border-grey-200 hover:border-grey-300 focus-visible:border-green focus-visible:ring-green",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown size={dense ? 14 : 16} strokeWidth={1.8} className="shrink-0 text-grey-400" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});

export const SelectContent = forwardRef<
  ElementRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(function SelectContent({ className, children, position = "popper", ...props }, ref) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        className={cn(
          "z-[1000] overflow-hidden rounded-card border border-grey-200 bg-white shadow-float",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
          position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1",
          className,
        )}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" && "w-full min-w-[var(--radix-select-trigger-width)]",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});

export const SelectItem = forwardRef<
  ElementRef<typeof SelectPrimitive.Item>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(function SelectItem({ className, children, ...props }, ref) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-[6px] py-2 pl-3 pr-8 text-[14px] text-grey-800 outline-none",
        "focus:bg-green-bg focus:text-grey-900 data-[state=checked]:font-medium",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <span className="absolute right-2 flex items-center">
        <SelectPrimitive.ItemIndicator>
          <Check size={15} strokeWidth={2} className="text-green" />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  );
});
